import AsyncBlockService from 'services/loader-handler-services/async-block-service';
import EF from 'ef/ef';
import Backbone from 'backbone';
import Block from 'models/block';
import BlockCollection from 'collections/block_collection';
import utils from 'lib/utils';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import LoadService from 'services/loader-handler-services/load-service';
import LocaleService from 'services/loader-handler-services/locale-service';
import _ from 'lodash';

const ContentService = function () {
  if (ContentService.prototype._singleton) {
    return ContentService.prototype._singleton;
  }

  ContentService.prototype._singleton = this;
  this.files = {
    _groups: './content/groups.json',
    _modules: './content/modules.json',
    _moduleContent: './content/module-content.json',
  };

  this.initialize = function () {
    const split = CourseSettingsService.get('split_module');
    if (split) {
      this.files._modules = `./modules/${split}/content/module.json`;
      this.files._moduleContent = `./modules/${split}/content/module-content.json`;
    }
    return LoadService.load(this.files).then(data => {
      Object.defineProperty(this, 'groups', {
        get: function () {
          return data._groups;
        },
      });
      Object.defineProperty(this, 'modules', {
        get: function () {
          return data._modules;
        },
      });
      Object.defineProperty(this, 'moduleContent', {
        get: function () {
          return data._moduleContent;
        },
      });
    });
  };

  this.applyPageContent = function (page) {
    let m, a, p;
    const linkedPage = page.get('link');
    if (linkedPage) {
      [m, a, p] = [linkedPage.module, linkedPage.activity, linkedPage.page];
    } else {
      [m, a, p] = [page.module.get('key'), page.activity.get('key'), page.get('key')];
    }
    return this.applyContentAndLocale(page, `${m}.${a}.${p}`).then(() => {
      this.addForgePathsToPage(page);
      return Promise.resolve(page);
    });
  };
  this.addForgePathsToContentRegion = function (regionName, region) {
    if (EF.forgeMode && region) {
      // Precaution for deeply nested region objects
      Object.entries(region).forEach(([key, value]) => {
        if (key === 'blocks') {
          return this.addPaths(regionName, region);
        }
        this.addForgePathsToContentRegion(regionName, value);
      });
    }
  };
  this.traverseBlocks = function (obj, path, contentRegion) {
    for (const i in obj) {
      if (obj[i] !== null && typeof obj[i] === 'object') {
        if (path[path.length - 1] === 'blocks') {
          obj[i].forge_path = `${path.join()},${i}`;
          obj[i].content_region = contentRegion;
        }
        const newpath = !utils.isEmpty(path) ? [...path] : [];
        newpath.push(i);
        if (i === 'blocks') {
          obj[i].forge_path = newpath.join();
        }
        this.traverseBlocks(obj[i], newpath, contentRegion);
      }
    }
  };
  this.addForgePathsToPage = function (page, contentRegion = '#page-container') {
    if (EF.forgeMode && page) {
      page.get('blocks').forge_path = 'blocks';
      page.get('blocks').models.forEach((pageBlock, idx) => {
        pageBlock.attributes.forge_path = `blocks,${idx}`;
        pageBlock.attributes.content_region = contentRegion;
        this.traverseBlocks(pageBlock.attributes, ['blocks', idx], contentRegion);
      });
    }
  };

  this.applyContentAndLocale = function (container, path, subView = false) {
    return this.applyContent(container, path).then(containerParam => {
      LocaleService.addDefaultResources(path, subView);
      return Promise.resolve(containerParam);
    });
  };

  this.applyDirectPageContent = function (page, content) {
    return this.buildBlockCollection(content && content.blocks).then(blocks => {
      if (content && content.blocks) {
        content.blocks = blocks;
      }
      page.set(content);
      this.reloadPageContentInPlace(page);
    });
  };

  this.reloadPageContentInPlace = function (page) {
    if (EF.forgeMode) {
      this.addForgePathsToPage(page);
    }
  };

  this.applyContent = function (container, path) {
    // Deep clone, so that the original source is not mutated
    //   This was causing some of the translations, to get translated twice and spit out error text
    const content = _.cloneDeep(utils.dotStringToObject(path, this.moduleContent));
    return this.buildBlockCollection(content && content.blocks).then(blocks => {
      if (content && content.blocks) {
        content.blocks = blocks;
      }
      container.set(content);
      return Promise.resolve(container);
    });
  };

  this.buildBlockCollection = function (rawBlocks) {
    if (!rawBlocks) {
      return new Promise(resolve => resolve());
    }
    return AsyncBlockService.load(rawBlocks.map(b => b.type || b.get('type'))).then(() => {
      return Promise.resolve(
        rawBlocks instanceof Backbone.Collection
          ? rawBlocks
          : new BlockCollection(
              rawBlocks
                .map(block => {
                  if (typeof block === 'string' && this.moduleContent['base-blocks'] !== undefined) {
                    block = this.moduleContent['base-blocks'][block];
                  }
                  if (this.blockAvailable(block)) {
                    return new Block(block);
                  }
                })
                .filter(b => b !== undefined)
            )
      );
    });
  };

  this.blockAvailable = function (block) {
    let available = true;
    if (block.completed_modules_required) {
      available = block.completed_modules_required <= EF.modules.completedModuleCount();
    }
    if (available && block.certified !== undefined) {
      available = block.certified === Boolean(EF.enrollment.get('certified'));
    }
    if (available && block.fullSCORM !== undefined) {
      available = block.fullSCORM === Boolean(EF.config.get('fullSCORM'));
    }

    return available;
  };
  this.addPaths = (regionName, region) => {
    region.blocks.forEach((block, index) => {
      block.forge_path = `blocks,${index}`;
      block.content_region = regionName;
      this.traverseBlocks(block, ['blocks', index], regionName);
    });
  };
};

export default new ContentService();
